import { api, isRequestCancellation } from "@shared/api/client";

const ADMIN_PANEL_CHAT_TIMEOUT_MS = 45000;

export function isAdminPanelChatTimeoutError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    (error as { code?: string }).code === "ECONNABORTED"
  );
}

async function getWithRetry<T>(url: string): Promise<T> {
  try {
    const { data } = await api.get<T>(url, {
      timeout: ADMIN_PANEL_CHAT_TIMEOUT_MS,
    });

    return data;
  } catch (error) {
    // Las cancelaciones por logout/navegacion no deben reintentarse ni ensuciar consola.
    if (isRequestCancellation(error)) {
      throw error;
    }

    throw error;
  }
}

export type AdminPanelChatUser = {
  id: number;
  nombre: string;
  correo: string;
  ultimo: string;
  ultimo_en?: string | null;
  unread_count: number;
};

export type AdminPanelAttachment = {
  id: number;
  nombre: string;
  mime?: string | null;
  size?: number | null;
};

export type AdminPanelChatMessage = {
  id: number;
  client_id?: string;
  client_order?: number;
  from: "me" | "other";
  emisor_id: number;
  mensaje: string;
  creado_en: string;
  leido: boolean;
  adjunto?: AdminPanelAttachment | null;
  status?: "sending" | "sent" | "error";
};

export async function getAdminPanelChatUsers(): Promise<AdminPanelChatUser[]> {
  return getWithRetry<AdminPanelChatUser[]>("/admin-panel/chat/users");
}

export async function getAdminPanelChatMessage(
  userId: number,
): Promise<AdminPanelChatMessage[]> {
  const data = await getWithRetry<AdminPanelChatMessage[]>(
    `/admin-panel/chat/messages/${userId}`,
  );

  return data.map((msg) => ({
    ...msg,
    status: "sent",
  }));
}

export async function sendAdminPanelChatMessage(
  userId: number,
  mensaje: string,
  clientId: string,
  archivo?: File | null,
): Promise<AdminPanelChatMessage> {
  const fd = new FormData();

  fd.append("user_id", String(userId));
  fd.append("client_id", clientId);

  if (mensaje.trim()) {
    fd.append("mensaje", mensaje);
  }

  if (archivo) {
    fd.append("archivo", archivo);
  }

  const { data } = await api.post<{
    ok: boolean;
    client_id: string | null;
    message: AdminPanelChatMessage;
  }>("/admin-panel/chat/messages", fd, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return {
    ...data.message,
    client_id: data.client_id ?? clientId,
    status: "sent",
  };
}

export async function markAdminPanelChatAsRead(userId: number): Promise<void> {
  await api.post(`/admin-panel/chat/read/${userId}`);
}

export async function loadAdminPanelChatAttachmentPreview(
  id: number,
): Promise<Blob> {
  const res = await api.get(`/admin-panel/chat/attachments/${id}/download`, {
    params: { inline: 1 },
    responseType: "blob",
  });

  return res.data as Blob;
}

export async function downloadAdminPanelChatAttachment(
  id: number,
): Promise<void> {
  const res = await api.get(`/admin-panel/chat/attachments/${id}/download`, {
    responseType: "blob",
  });

  const blob = res.data as Blob;
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = `adjunto-${id}`;
  document.body.appendChild(a);
  a.click();

  a.remove();
  window.URL.revokeObjectURL(url);
}
